import React, { useEffect, useState } from "react";
import BackHeader from "@components/header/BackHeader";
import styled from "@emotion/styled";
import { useNavigate, useParams } from "react-router-dom";
import UserImage from "@components/item/UserImage";
import StarRating from "@components/item/StarRating";
import { FormProvider, useForm } from "react-hook-form";
import BasicButton from "@components/button/BasicButton";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useApiRequest } from "@utils/ApiRequest";
import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import { reviewSchema } from "@libs/zod/zodVaildation";
import { queryClient } from "@libs/reactQuery/queryClient";
import BasicModal from "@components/modal/BasicModal";
import { ModalContent } from "@components/modal/ModalContent";
import useUserInfoStore from "@store/useUserInfoStore";

const Container = styled.form({
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  gap: "4.2rem",
  padding: "0 2rem",
  height: "calc(100dvh - 5.6rem)",
  maxHeight: "calc(100dvh - 5.6rem)",
});

const UserInfoWrapper = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "0.8rem",
  justifyContent: "center",
  alignItems: "center",
  alignSelf: "center",
  fontWeight: theme.fontWeight.medium,
  lineHeight: "16px",
  padding: "2.4rem 0 0",
}));

const ProductInfoWrapper = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "1.8rem",
  lineHeight: "16px",
  padding: "1.8rem 0 2.4rem",
  borderBottom: `1px solid ${theme.color.border_gray}`,

  ".title": {
    fontWeight: theme.fontWeight.medium,
  },
}));

const RatingWrapper = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "1.8rem",
  lineHeight: "16px",
  fontWeight: theme.fontWeight.medium,
}));

const ReviewDetailWrapper = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "1.8rem",
  lineHeight: "16px",
  fontWeight: theme.fontWeight.medium,
}));

const CustomTextarea = styled.textarea(({ theme }) => ({
  border: `1px solid ${theme.color.light_gray}`,
  resize: "none",
  padding: "1.6rem",
  fontSize: "1.4rem",
  lineHeight: "2.1rem",
  height: "20.8rem",
}));

interface ReviewFormdata {
  rate: number;
  review_text: string;
  seller_id: number;
  order_id: number;
}

const AddReview = () => {
  const params = useParams();
  const orderId = Number(params.orderId);
  const orderSellerId = Number(params.orderSellerId);
  const { postReview, getProductDataForReview, getHasReview } = useApiRequest();
  const language = localStorage.getItem("language");
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { userInfo } = useUserInfoStore();

  const { data: productData } = useQuery({
    queryKey: ["productData", orderSellerId],
    queryFn: () => getProductDataForReview(orderId, orderSellerId),
    enabled: !!orderSellerId,
  });

  // 리뷰작성 여부 조회
  const { data: hasReview } = useQuery({
    queryKey: ["hasReview"],
    queryFn: () => getHasReview(orderId, orderSellerId),
    enabled: !!orderSellerId,
  });

  const mutation = useMutation({
    mutationFn: (data: ReviewFormdata) => postReview(data),
    onSuccess: () => {
      // navigate(`/order-detail/${orderId}`);
      navigate("/review-success");

      queryClient.invalidateQueries({ queryKey: ["myReviewList"] });
    },
  });

  const method = useForm({
    resolver: zodResolver(reviewSchema),

    defaultValues: {
      rate: 0,
      review_text: "",
    },
  });

  const { handleSubmit, watch, setValue } = method;

  const onSubmit = (data: { rate: number; review_text: string }) => {
    if (!orderSellerId) {
      console.error("Invalid orderSellerId");
      return;
    }

    mutation.mutate({
      order_id: orderId,
      seller_id: orderSellerId,
      rate: data.rate,
      review_text: data.review_text,
    });
  };

  useEffect(() => {
    if (hasReview) {
      setOpen(true);
    }
  }, [hasReview]);

  return (
    <FormProvider {...method}>
      <BackHeader title="리뷰 작성" />

      <Container onSubmit={handleSubmit(onSubmit)}>
        <>
          <UserInfoWrapper>
            <UserImage src={productData?.seller_profile_image} size="4.8rem" />
            <span>{productData?.seller_nickname}</span>
          </UserInfoWrapper>
          <ProductInfoWrapper>
            <span className="title">{t("구매한 상품")}</span>
            <span>
              {language === "en" && productData?.product_name_en !== ""
                ? productData?.product_name_en
                : productData?.product_name_ko}{" "}
              {productData?.product_total_count !== 1 &&
                `외 ${productData?.total_product_count}건`}
            </span>
          </ProductInfoWrapper>
          <RatingWrapper>
            <span>{t("별점")}</span>
            <StarRating
              rate={watch("rate")}
              onChange={(value) => setValue("rate", value)}
            />
          </RatingWrapper>
          <ReviewDetailWrapper>
            <span>{t("리뷰 작성")}</span>
            <CustomTextarea
              maxLength={1000}
              value={watch("review_text")}
              onChange={(e) => setValue("review_text", e.target.value)}
            />
          </ReviewDetailWrapper>
        </>

        <div
          style={{
            paddingBottom: "2.4rem",
          }}>
          <BasicButton
            width="100%"
            type="submit"
            disabled={!watch("review_text")}
            title="작성 완료"
          />
        </div>

        {/* <BasicModal
          open={open}
          setOpen={setOpen}
          activeEvent={() => {
            setOpen(false);
            navigate(`/review/${userInfo?.id}`);
          }}
          activeName="작성한 리뷰 보러가기"
          maskClosable={false}>
          <ModalContent mainMessage="리뷰 작성이 완료된 상품입니다." />
        </BasicModal> */}
      </Container>
    </FormProvider>
  );
};

export default AddReview;
